import { Contrast, WifiOff } from 'lucide-react';
import { getLayer } from '../core/model/document';
import { TOOL_BY_ID } from '../core/tools';
import { zoomLabel } from '../core/view';
import { useEditorStore } from '../state/editorStore';

/** Status bar: the always-visible truth about the current document and view. */
export function StatusBar() {
  const doc = useEditorStore((s) => s.doc);
  const zoom = useEditorStore((s) => s.viewport.zoom);
  const pointer = useEditorStore((s) => s.pointer);
  const activeTool = useEditorStore((s) => s.activeTool);
  const engineId = useEditorStore((s) => s.engineId);
  const online = useEditorStore((s) => s.online);
  const maskEditing = useEditorStore((s) => s.maskEditing);

  // Painting the mask instead of the layer changes what a stroke does, so the
  // status bar says so: the canvas looks the same and the tool label does not.
  const maskLayer = maskEditing ? getLayer(doc, maskEditing) : undefined;

  const tool = TOOL_BY_ID[activeTool];

  return (
    <footer
      className="flex h-6 shrink-0 items-center gap-4 border-t border-line bg-surface px-3 text-[11px] text-muted"
      data-testid="statusbar"
    >
      <span className="text-ink">{doc.name}</span>
      <span className="tabular-nums">
        {doc.width} × {doc.height}
      </span>
      <span className="tabular-nums" data-testid="status-zoom">
        {zoomLabel(zoom)}
      </span>
      <span className="tabular-nums" data-testid="status-pointer">
        {pointer ? `${Math.round(pointer.x)}, ${Math.round(pointer.y)}` : '—'}
      </span>
      <span className="ml-auto">{tool?.label ?? activeTool}</span>
      {maskLayer ? (
        <span className="flex items-center gap-1 text-accent" data-testid="status-mask">
          <Contrast size={11} aria-hidden="true" />
          mask: {maskLayer.name}
        </span>
      ) : null}
      {/* Offline is a fact about the app the user is entitled to know: work keeps
          saving to IndexedDB, but export-to-clipboard and nothing networked will
          behave differently. */}
      {online ? null : (
        <span className="flex items-center gap-1 text-warn" data-testid="status-offline">
          <WifiOff size={11} aria-hidden="true" />
          offline
        </span>
      )}
      <span className="text-faint">engine: {engineId ?? 'none'}</span>
    </footer>
  );
}
